/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React from 'react';
import PropTypes from "prop-types";
import { EuiSuperSelect } from '../../form';
import { EuiColorPaletteDisplay } from '../color_palette_display';
export var EuiColorPalettePicker = ({
  className,
  compressed = false,
  disabled,
  fullWidth = false,
  isInvalid = false,
  onChange,
  readOnly = false,
  valueOfSelected,
  palettes,
  append,
  prepend,
  selectionDisplay = 'palette',
  ...rest
}) => {
  const getPalette = ({
    type,
    palette,
    title
  }) => {
    return <EuiColorPaletteDisplay type={type} palette={palette} title={title} />;
  };

  const paletteOptions = palettes.map(item => {
    const {
      type,
      value,
      title,
      palette,
      ...rest
    } = item;
    const paletteForDisplay = item.type !== 'text' ? getPalette(item) : null;
    return {
      value: String(value),
      inputDisplay: selectionDisplay === 'title' || type === 'text' ? title : paletteForDisplay,
      dropdownDisplay: <div className="euiColorPalettePicker__item">
            {title && type !== 'text' && // Accessible labels are managed by color_palette_display_fixed and
        // color_palette_display_gradient. Adding the aria-hidden attribute
        // here to ensure screen readers don't speak the listbox options twice.
        <div aria-hidden="true" className="euiColorPalettePicker__itemTitle">
                {title}
              </div>}
            {type === 'text' ? title : paletteForDisplay}
          </div>,
      ...rest
    };
  });
  return <EuiSuperSelect className={className} options={paletteOptions} valueOfSelected={valueOfSelected} onChange={onChange} hasDividers isInvalid={isInvalid} compressed={compressed} disabled={disabled} readOnly={readOnly} fullWidth={fullWidth} append={append} prepend={prepend} {...rest} />;
};
EuiColorPalettePicker.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  compressed: PropTypes.bool,
  fullWidth: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isLoading: PropTypes.bool,
  readOnly: PropTypes.bool,
  name: PropTypes.string,

  /**
     * Creates an input group with element(s) coming before input.
     * `string` | `ReactElement` or an array of these
     */
  prepend: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired).isRequired]),

  /**
     * Creates an input group with element(s) coming after input.
     * `string` | `ReactElement` or an array of these
     */
  append: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired).isRequired]),

  /**
     * Creates a semantic label ID for the `div[role="listbox"]` that's opened on click or keypress.
     * __Generated and passed down by `EuiSuperSelect`.__
     */
  screenReaderId: PropTypes.string,
  valueOfSelected: PropTypes.any,

  /**
       * Classes for the context menu item
       */
  itemClassName: PropTypes.string,

  /**
       * You must pass an `onChange` function to handle the update of the value
       */
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,

  /**
       * Controls whether the options are shown. Default: false
       */
  isOpen: PropTypes.bool,

  /**
       * Optional props to pass to the underlying [EuiPopover](/#/layout/popover).
       * Allows fine-grained control of the popover dropdown menu, including
       * `repositionOnScroll` for EuiSuperSelects used within scrollable containers,
       * and customizing popover panel styling.
       *
       * Does not accept a nested `popoverProps.isOpen` property - use the top level
       * `isOpen` API instead.
       */
  popoverProps: PropTypes.any,

  /**
       *  Specify what should be displayed after a selection: a `palette` or `title`
       */
  selectionDisplay: PropTypes.oneOf(["palette", "title"]),

  /**
       * An array of one of the following objects: #EuiColorPalettePickerPaletteText, #EuiColorPalettePickerPaletteFixed, #EuiColorPalettePickerPaletteGradient
       */
  palettes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
    /**
       *  For storing unique value of item
       */
    value: PropTypes.string.isRequired,

    /**
       *  The name of your palette
       */
    title: PropTypes.string.isRequired,

    /**
       * `text`: a text only option (a title is required).
       */
    type: PropTypes.oneOf(["text"]).isRequired,

    /**
       * Array of color `strings` or an array of #ColorStop. The stops must be numbers in an ordered range.
       */
    palette: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string.isRequired).isRequired, PropTypes.arrayOf(PropTypes.shape({
      stop: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired
    }).isRequired).isRequired]),
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string
  }).isRequired, PropTypes.shape({
    /**
       *  For storing unique value of item
       */
    value: PropTypes.string.isRequired,

    /**
       *  The name of your palette
       */
    title: PropTypes.string,

    /**
       * `fixed`: individual color blocks
       */
    type: PropTypes.oneOf(["fixed"]).isRequired,

    /**
       * Array of color `strings` or an array of #ColorStop. The stops must be numbers in an ordered range.
       */
    palette: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string.isRequired).isRequired, PropTypes.arrayOf(PropTypes.shape({
      stop: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired
    }).isRequired).isRequired]).isRequired,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string
  }).isRequired, PropTypes.shape({
    /**
       *  For storing unique value of item
       */
    value: PropTypes.string.isRequired,

    /**
       *  The name of your palette
       */
    title: PropTypes.string,

    /**
       * `gradient`: each color fades into the next
       */
    type: PropTypes.oneOf(["gradient"]).isRequired,

    /**
       * Array of color `strings` or an array of #ColorStop. The stops must be numbers in an ordered range.
       */
    palette: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string.isRequired).isRequired, PropTypes.arrayOf(PropTypes.shape({
      stop: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired
    }).isRequired).isRequired]).isRequired,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string
  }).isRequired]).isRequired).isRequired
};

try {
  EuiColorPalettePicker.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/color_picker/color_palette_picker/color_palette_picker.tsx',
    description: '',
    displayName: 'EuiColorPalettePicker',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(event?: FocusEvent<Element, Element>) => void'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(event?: FocusEvent<Element, Element>) => void'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'You must pass an `onChange` function to handle the update of the value',
        name: 'onChange',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(value: string) => void'
        }
      },
      fullWidth: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'fullWidth',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: null,
        description: '',
        name: 'isLoading',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      name: {
        defaultValue: null,
        description: '',
        name: 'name',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      compressed: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      readOnly: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'readOnly',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isOpen: {
        defaultValue: null,
        description: 'Controls whether the options are shown. Default: false',
        name: 'isOpen',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isInvalid: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'isInvalid',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      prepend: {
        defaultValue: null,
        description: 'Creates an input group with element(s) coming before input.\n' + '`string` | `ReactElement` or an array of these',
        name: 'prepend',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      append: {
        defaultValue: null,
        description: 'Creates an input group with element(s) coming after input.\n' + '`string` | `ReactElement` or an array of these',
        name: 'append',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      screenReaderId: {
        defaultValue: null,
        description: 'Creates a semantic label ID for the `div[role="listbox"]` that\'s opened on click or keypress.\n' + '__Generated and passed down by `EuiSuperSelect`.__',
        name: 'screenReaderId',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      valueOfSelected: {
        defaultValue: null,
        description: '',
        name: 'valueOfSelected',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      itemClassName: {
        defaultValue: null,
        description: 'Classes for the context menu item',
        name: 'itemClassName',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      popoverProps: {
        defaultValue: null,
        description: 'Optional props to pass to the underlying [EuiPopover](/#/layout/popover).\n' + 'Allows fine-grained control of the popover dropdown menu, including\n' + '`repositionOnScroll` for EuiSuperSelects used within scrollable containers,\n' + 'and customizing popover panel styling.\n' + '\n' + 'Does not accept a nested `popoverProps.isOpen` property - use the top level\n' + '`isOpen` API instead.',
        name: 'popoverProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Partial<CommonProps & Omit<EuiPopoverProps, "isOpen">>'
        }
      },
      selectionDisplay: {
        defaultValue: {
          value: 'palette'
        },
        description: 'Specify what should be displayed after a selection: a `palette` or `title`',
        name: 'selectionDisplay',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/color_picker/color_palette_picker/color_palette_picker.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"title" | "palette"',
          value: [{
            value: '"title"'
          }, {
            value: '"palette"'
          }]
        }
      },
      palettes: {
        defaultValue: null,
        description: 'An array of one of the following objects: #EuiColorPalettePickerPaletteText, #EuiColorPalettePickerPaletteFixed, #EuiColorPalettePickerPaletteGradient',
        name: 'palettes',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/color_picker/color_palette_picker/color_palette_picker.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'EuiColorPalettePickerPaletteProps[]'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'ButtonHTMLAttributes', 'EuiSuperSelectControlProps']
  };
} catch (e) {}